import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import axios from 'axios';

import { Modal } from 'antd';
import { useQuery } from '@utils/CustomHooks';

import { displayErrorMessage } from '@utils/DisplayMessageUtility';
import { ErrorParser } from '@utils/ErrorMessageUtils';

import { AuthRepository } from '@common/domain/repositories/AuthRepository';
import { socialMediaLogin } from '@common/redux/auth/actions';
import { ISocialMediaLogin } from '@common/redux/auth/interface';
import { loginLoaderSelector } from '@common/redux/auth/selectors';

import { Google, Logo } from '@assets/index';
import { translate } from "@translations/Translater";

import PrimaryButton from '@components/atoms/Button/PrimaryButton';
import { WFImg, WFProfilePic } from '@components/index';
import Text, { FontFamilyVariant, TextType } from '@components/Text';

import { AUTH_FLOW, GOOGLE_USERINFO_ENDPOINT, SOCIAL_MEDIA, USER_EXIST } from '@constants/config';

import { authMessages } from '@containers/Auth/message';
import LegalLinks from '@presentation/auth/components/LegalLinks';
import config from '@config/index';
import { TagManagerService } from "@webServices/TagManagerService";
import { EnvTypes } from '@constants/config';
import './styles.less';


interface IProps {
    socialMediaLogin: (payload: ISocialMediaLogin) => void;
    loginLoader: boolean;
    flow?: string;
    instituteCode?: string;
}

const GoogleAuthButton = (props: IProps) => {

    const { t } = useTranslation();
    const { socialMediaLogin, loginLoader, flow, instituteCode } = props;
    const [existUser, setExistUser] = useState("");
    const [accessToken, setAccessToken] = useState("");
    const [userInfo, setUserInfo] = useState(null);
    const [email, setEmail] = useState("");
    const [showModal, setShowModal] = useState(false);
    const query = useQuery();
    const instituteCodequery = query.get("instituteCode");

    // @ts-ignore
    const client = window?.google?.accounts?.oauth2?.initTokenClient({
        client_id: config.GOOGLE_CLIENT_ID,
        scope: 'profile email',
        callback: async (response) => {
            try {
                const userInfo = await axios.get(`${GOOGLE_USERINFO_ENDPOINT}${response.access_token}`);
                const checkUser = await AuthRepository.checkUserExist({ userName: userInfo.data.email });
                setExistUser(checkUser.userExists ? USER_EXIST.USER_EXISTED : USER_EXIST.USER_NOT_EXISTED);
                setAccessToken(response.access_token);
                setEmail(userInfo.data.email);
                setUserInfo(userInfo.data);
                setShowModal(true);
                if (checkUser.userExists && flow === AUTH_FLOW.SIGN_IN) {
                    socialMediaLogin({ token: response.access_token, type: SOCIAL_MEDIA.GOOGLE, userInfo: userInfo.data });
                }
                if (!checkUser.userExists && flow === AUTH_FLOW.SIGN_UP) {
                    socialMediaLogin({ token: response.access_token, type: SOCIAL_MEDIA.GOOGLE, userInfo: userInfo.data, ...(instituteCode || instituteCodequery) && { instituteCode: instituteCode || instituteCodequery } });
                }
            } catch (e) {
                displayErrorMessage(ErrorParser(e.code));
            }
        }
    });

    const onClickGoogleLogin = () => {
        // @ts-ignore
        client?.requestAccessToken();
    }

    const toggleModalVisible = () => {
        setShowModal(!showModal);
    }

    const onClickGoogleSignUp = () => {
        if(config.GOOGLE_TAG_MANAGER_CONFIG.env === EnvTypes.PROD) {
            TagManagerService.pushEvent('signup',{
            email: email,
            });
        }
        socialMediaLogin({ token: accessToken, type: SOCIAL_MEDIA.GOOGLE, userInfo });
    }

    const getModalMessage = () => {
        if (existUser === USER_EXIST.USER_EXISTED && flow === AUTH_FLOW.SIGN_UP) {
            return translate(t, authMessages.alreadyRegistered);
        }
        if (existUser === USER_EXIST.USER_NOT_EXISTED && flow === AUTH_FLOW.SIGN_IN) {
            return translate(t, authMessages.notRegistered);
        }
    }

    const getConfirmationButtonText = () => {
        if (existUser === USER_EXIST.USER_EXISTED && flow === AUTH_FLOW.SIGN_UP) {
            return translate(t, authMessages.signInContinue);
        }
        if (existUser === USER_EXIST.USER_NOT_EXISTED && flow === AUTH_FLOW.SIGN_IN) {
            return translate(t, authMessages.signUpContinue);
        }
    }

    return (
        <>
            <div className="google_auth_container" onClick={onClickGoogleLogin}>
                <WFImg alt="google_logo" src={Google} />
                <Text
                    className="google_sign_in_text"
                    fontFamilyVariant={FontFamilyVariant.regular}
                    text={flow === AUTH_FLOW.SIGN_IN ? translate(t, authMessages.signInGoogle) : translate(t, authMessages.signUpGoogle)}
                    textType={TextType.h8}
                />
            </div>
            {((existUser === USER_EXIST.USER_EXISTED && flow === AUTH_FLOW.SIGN_UP) || (flow === AUTH_FLOW.SIGN_IN && existUser === USER_EXIST.USER_NOT_EXISTED)) && <Modal
                open={showModal}
                onCancel={toggleModalVisible}
                footer={null}
            >
                <div className="confirmation_container">
                    <WFImg alt="wo_logo" src={Logo} />
                    <Text
                        className="allow_google"
                        fontFamilyVariant={FontFamilyVariant.bold}
                        text={getModalMessage()}
                        textType={TextType.h6}
                    />
                    <div className="pic_email_container">
                        <WFProfilePic
                            alt="member-profile-image"
                            imageUrl={userInfo?.picture || userInfo?.name}
                            name={userInfo?.name}
                            retry={{ count: 0 }}
                            size={20}
                        />
                        <Text
                            className="email"
                            fontFamilyVariant={FontFamilyVariant.bold}
                            text={email}
                            textType={TextType.h8}
                        />
                    </div>
                    {flow === AUTH_FLOW.SIGN_IN && <Text
                        className="email"
                        fontFamilyVariant={FontFamilyVariant.regular}
                        text={translate(t, authMessages.googleSignUpDescription)}
                        textType={TextType.h8}
                    />}
                    <PrimaryButton
                        className="auth_cmn__button"
                        htmlType="submit"
                        onClick={onClickGoogleSignUp}
                        type="primary"
                        loading={loginLoader}
                    >
                        {getConfirmationButtonText()}
                    </PrimaryButton>
                </div>
                <LegalLinks />  
            </Modal>
            } 
        </>
    )
}

const mapDispatchToProps = (dispatch: any) => ({
    socialMediaLogin: (payload: ISocialMediaLogin) => dispatch(socialMediaLogin(payload))
});

const mapStateToProps = (state: any) => ({
    loginLoader: loginLoaderSelector(state),
});

export default connect(mapStateToProps, mapDispatchToProps)(GoogleAuthButton);

